<template lang="html">
  <div column class="full-height-page">
    <v-layout justify-space-between align-center>
      <div class="px-3">
        <sc-h4>
          Filing Instructions
        </sc-h4>
      </div>

      <!-- Position Control Menu -->
      <div class="position-controls">
        <v-menu content-class="position-menu">
          <v-btn icon class="icon-btn" slot="activator">
            <img
              v-if="currentPosition"
              :src="currentPosition.icon"
              width="20"
            />

            <v-icon v-else>more_vert</v-icon>
          </v-btn>

          <v-list>
            <v-list-tile
              v-for="pos in positions"
              :key="pos.value"
              @click="updatePosition(pos.value)"
              :disabled="pos.value === position"
            >
              <v-list-tile-title>
                <v-layout justify-start align-center>
                  <img :src="pos.icon" width="20" class="mr-3" />
                  <span>
                    {{ pos.title }}
                  </span>
                </v-layout>
              </v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>

        <v-tooltip bottom>
          <v-btn icon class="icon-btn" @click="closeWindow" slot="activator">
            <v-icon>close</v-icon>
          </v-btn>
          <span>
            Close
          </span>
        </v-tooltip>
      </div>
    </v-layout>

    <v-layout
      class="py-3"
      v-if="!taxReturn || !pageImages"
      align-center
      justify-center
    >
      <sc-small> Loading filing instructions... </sc-small>
    </v-layout>

    <div
      v-else-if="
        taxReturn.filing_instructions &&
          taxReturn.filing_instructions.length > 0
      "
    >
      <v-layout column align-center>
        <div
          v-for="{ page } in taxReturn.filing_instructions"
          :key="page"
          class="page-container"
        >
          <div
            v-if="pageImages.find(i => i.page === page)"
            class="page-wrapper"
          >
            <img
              :src="pageImages.find(i => i.page === page).thumbnail"
              class="page"
              :class="{
                [`page-${page}`]: true,
              }"
            />
            <div class="bounding-boxes-overlay">
              <v-tooltip
                v-for="(block, blockIndex) in getBlocksByPage(page)"
                :disabled="!showTooltip"
                :key="blockIndex"
                bottom
              >
                <div
                  slot="activator"
                  class="bounding-box"
                  :class="{
                    highlighted: highlightedBlockNumbers.includes(
                      block.blockNumber,
                    ),
                    [`block-${block.blockNumber}`]: true,
                  }"
                  :style="getBoundingBoxStyle(block.boundingBox)"
                />
                <span>{{ block.blockNumber }}</span>
              </v-tooltip>
            </div>
          </div>
        </div>
      </v-layout>
    </div>

    <v-layout v-else class="py-3" align-center justify-center>
      <sc-small> No filing instructions to show. </sc-small>
    </v-layout>
  </div>
</template>

<script>
import API from '@/api';
import { generatePdfThumbnails } from '@/utils/pdf';
import { PDFDocument } from 'pdf-lib';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import isEmpty from 'lodash/isEmpty';

export default {
  mixins: [FirmPreferenceMixins],

  data() {
    return {
      taxReturn: null,
      pageImages: null,
      position: null,
      filingInstructionsBlocks: [],
      isAnalyzing: false,
      highlightedBlockNumbers: [],
      positions: [
        {
          value: 'bottom',
          title: 'Dock to bottom',
          icon: require('@/assets/icons/dock-bottom.svg'),
        },
        {
          value: 'left',
          title: 'Dock to left',
          icon: require('@/assets/icons/dock-left.svg'),
        },
        {
          value: 'right',
          title: 'Dock to right',
          icon: require('@/assets/icons/dock-right.svg'),
        },
      ],
      // For debugging use only
      showTooltip: false,
      // Message handler for cross-window communication
      handleParentMessage: null,
    };
  },

  computed: {
    currentPosition() {
      return this.positions.find(pos => pos.value === this.position);
    },
  },

  methods: {
    updatePosition(newPosition) {
      this.position = newPosition;

      // Send message to parent window
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          {
            type: 'filing-instructions-position-changed',
            position: newPosition,
          },
          window.location.origin,
        );

        // delay closing the window slightly to ensure the message is sent before the window is closed
        // TODO: Use ack message from parent window instead of timeout to ensure message is received before any action is taken
        setTimeout(() => {
          window.close();
        }, 300);
      } else {
        window.close();
      }
    },

    closeWindow() {
      // Send message to parent window to update visibility to false
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          {
            type: 'filing-instructions-visibility-changed',
            visible: false,
          },
          window.location.origin,
        );

        // delay closing the window slightly to ensure the message is sent before the window is closed
        // TODO: Use ack message from parent window instead of timeout to ensure message is received before any action is taken
        setTimeout(() => {
          window.close();
        }, 300);
      } else {
        window.close();
      }
    },

    async getTaxReturn() {
      // get tax return id from params
      const taxReturnId = this.$route.query.taxReturnId;
      const companyId = this.$route.query.companyId;

      try {
        const response = await API.taxReturn.getTaxReturn({
          id: taxReturnId,
          companyId,
        });

        this.taxReturn = response.data;
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem loading filing instructions.',
        });
      }
    },

    async loadPageImages() {
      const response = await API.integration.downloadDocument({
        companyId: this.taxReturn.company_id,
        link: this.taxReturn.original_file,
      });

      const pdfSrc = response.data.document.data.link;
      const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());

      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pdfData = await pdfDoc.saveAsBase64({ dataUri: true });
      this.pageImages = await generatePdfThumbnails(pdfData, 1000);
    },

    async loadFilingInstructionsAnalysis() {
      try {
        this.isAnalyzing = true;
        const response = await API.taxReturn.analyzeFilingInstructions({
          id: this.taxReturn.id,
          companyId: this.taxReturn.company_id,
          pages: this.taxReturn.filing_instructions.map(fi => fi.page),
        });
        this.filingInstructionsBlocks = response.data.data || [];
      } catch (error) {
        console.error('Error loading filing instructions analysis:', error);
        this.filingInstructionsBlocks = [];
      } finally {
        this.isAnalyzing = false;
      }
    },

    getBlocksByPage(page) {
      const blocks = this.filingInstructionsBlocks.filter(
        block => block.page === page,
      );
      return blocks;
    },

    getBoundingBoxStyle(boundingBox) {
      if (!boundingBox) return {};

      // Convert normalized coordinates to percentages
      return {
        left: `${boundingBox.left * 100}%`,
        top: `${boundingBox.top * 100}%`,
        width: `${boundingBox.width * 100}%`,
        height: `${boundingBox.height * 100}%`,
      };
    },

    scrollToBlockWithHighlights() {
      if (this.highlightedBlockNumbers.length === 0) {
        return;
      }

      // Get the smallest block number to scroll to
      const smallestBlockNumber = Math.min(...this.highlightedBlockNumbers);

      // Scroll to the block element
      this.$nextTick(() => {
        const blockElement = this.$el.querySelector(
          `.block-${smallestBlockNumber}`,
        );
        if (blockElement) {
          blockElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },
  },

  watch: {
    async highlightedBlockNumbers() {
      if (
        isEmpty(this.filingInstructionsBlocks) &&
        this.taxReturnUseFilingInstructionsParser
      ) {
        await this.loadFilingInstructionsAnalysis();
      }

      this.scrollToBlockWithHighlights();
    },
  },

  async mounted() {
    await this.getTaxReturn();
    await this.loadPageImages();

    if (this.taxReturnUseFilingInstructionsParser) {
      await this.loadFilingInstructionsAnalysis();
    }

    // Set up message listener for receiving highlighted blocks from parent window
    this.handleParentMessage = event => {
      // Verify the message origin for security
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === 'filing-instructions-highlighted-blocks') {
        const newBlockNumbers = event.data.highlightedBlockNumbers || [];
        this.highlightedBlockNumbers = newBlockNumbers;
      }
    };

    window.addEventListener('message', this.handleParentMessage);

    // Notify parent window that we're ready to receive messages
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(
        { type: 'filing-instructions-ready' },
        window.location.origin,
      );
    }
  },

  beforeDestroy() {
    // Clean up the message listener
    if (this.handleParentMessage) {
      window.removeEventListener('message', this.handleParentMessage);
    }
  },
};
</script>

<style lang="css" scoped>
.full-height-page {
  position: fixed;
  z-index: 1;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;

  overflow: auto;
  background-color: #eee;
}

.page-container {
  position: relative;
  display: inline-block;
}

.page-wrapper {
  position: relative;
  display: inline-block;
  max-width: 1000px;
}

.page {
  width: 100%;
  height: 100%;
  display: block;
}

.bounding-boxes-overlay {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
}

.bounding-box {
  position: absolute;

  &.highlighted {
    border-color: rgba(255, 193, 7, 1);
    background-color: rgba(255, 193, 7, 0.3);
    box-shadow: 0 0 8px rgba(255, 193, 7, 0.6);
  }
}

.position-menu {
  & >>> .v-list__tile--disabled img {
    opacity: 0.25;
  }
}
</style>
